// Iter46 — Admin → Escalation Chains.
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useTenants } from "../api/sitroom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEVERITIES = ["s1", "s2", "s3", "s4"];
const ROLES = ["responder", "escalation_manager", "tenant_admin", "platform_admin", "observer"];
const CHANNELS = ["in_app", "email", "sms", "slack", "teams"];

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

export default function AdminEscalationChains() {
  const addToast = useStore((s) => s.addToast);
  const [chains, setChains] = useState([]);
  const [editing, setEditing] = useState(null); // null|"new"|<id>
  const { data: tenants = [] } = useTenants();

  const load = async () => {
    try {
      const r = await axios.get(`${API}/admin/escalation-chains`);
      setChains(r.data || []);
    } catch (e) {
      addToast({ type: "error", title: "Failed to load chains", body: e?.response?.data?.detail || e.message });
    }
  };
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-escalation-chains">
      <div className="flex items-center justify-between mb-3">
        <div style={mono(10, "var(--dimmer, #9198b0)", 700, "0.8px")} className="uppercase">
          Escalation chains
        </div>
        <button
          type="button"
          onClick={() => setEditing("new")}
          className="px-2.5 py-1 rounded"
          style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: "pointer" }}
          data-testid="admin-chain-new-btn"
        >+ Add chain</button>
      </div>

      {chains.length === 0 && editing !== "new" ? (
        <div className="rounded-md p-4" style={{ background: "var(--bg-surface, #0d0f18)", border: "1px solid rgba(255,255,255,0.10)" }} data-testid="admin-chain-empty">
          <div style={mono(11, "var(--dimmer, #9198b0)")}>
            No escalation chains configured. The engine falls back to its hard-coded ladder (L1 responder → L2 escalation manager → L3 tenant admin).
          </div>
        </div>
      ) : null}

      <div className="space-y-3">
        {chains.map((c) => (
          <ChainCard key={c.id} chain={c} tenants={tenants}
            isEditing={editing === c.id}
            onEdit={() => setEditing(c.id)}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }} />
        ))}
        {editing === "new" && (
          <ChainCard chain={null} tenants={tenants}
            isEditing={true}
            onEdit={() => {}}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }} />
        )}
      </div>
    </div>
  );
}

const ChainCard = ({ chain, tenants, isEditing, onEdit, onCancel, onSaved }) => {
  const addToast = useStore((s) => s.addToast);
  const [severity, setSeverity] = useState(chain?.severity || "s1");
  const [tenantId, setTenantId] = useState(chain?.tenant_id || "");
  const [levels, setLevels] = useState(
    chain?.levels?.length
      ? chain.levels.map((l) => ({ ...l, notify_channels: l.notify_channels || ["in_app"] }))
      : [{ level: 1, role: "responder", delay_minutes: 0, notify_channels: ["in_app"] }]
  );
  const [busy, setBusy] = useState(false);

  const addLevel = () => {
    const nextLvl = Math.max(0, ...levels.map((l) => l.level)) + 1;
    setLevels([...levels, { level: nextLvl, role: "escalation_manager", delay_minutes: 15, notify_channels: ["in_app", "email"] }]);
  };
  const updateLevel = (idx, patch) => setLevels((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)));
  const removeLevel = (idx) => setLevels((prev) => prev.filter((_, i) => i !== idx));
  const toggleChannel = (idx, ch) => {
    setLevels((prev) => prev.map((l, i) => {
      if (i !== idx) return l;
      const has = (l.notify_channels || []).includes(ch);
      return { ...l, notify_channels: has ? l.notify_channels.filter((c) => c !== ch) : [...(l.notify_channels || []), ch] };
    }));
  };

  const save = async () => {
    const body = {
      severity,
      tenant_id: tenantId || null,
      levels: levels.map((l) => ({
        level: parseInt(l.level, 10),
        role: l.role,
        delay_minutes: parseInt(l.delay_minutes, 10) || 0,
        notify_channels: l.notify_channels || ["in_app"],
      })),
    };
    setBusy(true);
    try {
      if (chain?.id) await axios.patch(`${API}/admin/escalation-chains/${chain.id}`, body);
      else await axios.post(`${API}/admin/escalation-chains`, body);
      addToast({ type: "success", title: chain?.id ? "Chain updated" : "Chain added" });
      onSaved();
    } catch (e) {
      addToast({ type: "error", title: "Save failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!chain?.id) return;
    if (!window.confirm("Delete this escalation chain?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/escalation-chains/${chain.id}`);
      addToast({ type: "success", title: "Chain deleted" });
      onSaved();
    } catch (e) {
      addToast({ type: "error", title: "Delete failed", body: e?.response?.data?.detail || e.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-md p-4"
      style={{
        background: "var(--bg-surface, #0d0f18)",
        border: `1px solid ${isEditing ? "rgba(24,95,165,0.5)" : "rgba(255,255,255,0.10)"}`,
      }}
      data-testid={chain?.id ? `admin-chain-card-${chain.id}` : "admin-chain-card-new"}
    >
      {isEditing ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <div>
              <div className="uppercase mb-1" style={mono(8, "var(--dimmer, #9198b0)", 700, "0.8px")}>Severity</div>
              <select value={severity} onChange={(e) => setSeverity(e.target.value)}
                className="w-full px-2 py-1.5 rounded outline-none"
                style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
                data-testid="admin-chain-edit-severity">
                {SEVERITIES.map((s) => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
            <div>
              <div className="uppercase mb-1" style={mono(8, "var(--dimmer, #9198b0)", 700, "0.8px")}>Tenant scope</div>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)}
                className="w-full px-2 py-1.5 rounded outline-none"
                style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
                data-testid="admin-chain-edit-tenant">
                <option value="">— global —</option>
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name || t.id}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-2 mb-2">
            {levels.map((l, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 items-center p-2 rounded"
                style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.06)" }}
                data-testid={`admin-chain-edit-level-${idx}`}>
                <input type="number" value={l.level} onChange={(e) => updateLevel(idx, { level: e.target.value })}
                  className="col-span-1 px-2 py-1 rounded outline-none text-right"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }} />
                <select value={l.role} onChange={(e) => updateLevel(idx, { role: e.target.value })}
                  className="col-span-3 px-2 py-1 rounded outline-none"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}>
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
                <input type="number" placeholder="delay (min)" value={l.delay_minutes} onChange={(e) => updateLevel(idx, { delay_minutes: e.target.value })}
                  className="col-span-2 px-2 py-1 rounded outline-none text-right"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }} />
                <div className="col-span-5 flex flex-wrap gap-1">
                  {CHANNELS.map((ch) => {
                    const on = (l.notify_channels || []).includes(ch);
                    return (
                      <button key={ch} type="button" onClick={() => toggleChannel(idx, ch)}
                        className="px-1.5 py-0.5 rounded"
                        style={{
                          background: on ? "rgba(24,95,165,0.18)" : "transparent",
                          border: `1px solid ${on ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                          color: on ? "#9ec3ec" : "var(--text-dim, #c5cad8)",
                          ...mono(9, undefined, 700, "0.4px"),
                          textTransform: "uppercase",
                          cursor: "pointer",
                        }}
                        data-testid={`admin-chain-edit-level-${idx}-channel-${ch}`}>{ch}</button>
                    );
                  })}
                </div>
                <button type="button" onClick={() => removeLevel(idx)}
                  className="col-span-1 px-2 py-1 rounded"
                  style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
                  data-testid={`admin-chain-edit-level-${idx}-del`}>×</button>
              </div>
            ))}
            <button type="button" onClick={addLevel}
              className="px-2 py-1 rounded"
              style={{ background: "transparent", color: "var(--text-dim, #c5cad8)", border: "1px dashed rgba(255,255,255,0.18)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
              data-testid="admin-chain-edit-add-level">+ Add level</button>
          </div>

          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={onCancel}
              className="px-3 py-1.5 rounded"
              style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text-dim, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
              data-testid="admin-chain-edit-cancel">Cancel</button>
            <button type="button" onClick={save} disabled={busy}
              className="px-3 py-1.5 rounded"
              style={{ background: "#185FA5", color: "#fff", ...mono(10, undefined, 700, "0.4px"), border: "none", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}
              data-testid="admin-chain-edit-save">{busy ? "Saving…" : "Save chain"}</button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <span style={mono(11, "#9ec3ec", 700)}>{chain.severity.toUpperCase()}</span>
              <span style={mono(10, "var(--dimmer, #9198b0)")}>
                {chain.tenant_id ? `tenant: ${chain.tenant_id}` : "global"}
              </span>
              <span style={mono(10, "var(--dimmer, #9198b0)")}>{chain.levels?.length || 0} levels</span>
            </div>
            <div className="flex gap-1">
              <button type="button" onClick={onEdit}
                className="px-2 py-1 rounded"
                style={{ background: "transparent", color: "#9ec3ec", border: "1px solid rgba(24,95,165,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
                data-testid={`admin-chain-card-edit-${chain.id}`}>Edit</button>
              <button type="button" onClick={del}
                className="px-2 py-1 rounded"
                style={{ background: "transparent", color: "#ff9a99", border: "1px solid rgba(226,75,74,0.40)", ...mono(9, undefined, 700, "0.4px"), cursor: "pointer" }}
                data-testid={`admin-chain-card-delete-${chain.id}`}>Del</button>
            </div>
          </div>
          <div className="space-y-1">
            {(chain.levels || []).map((l) => (
              <div key={l.level} className="flex items-center gap-3 p-1.5 rounded"
                style={{ background: "rgba(255,255,255,0.025)" }}>
                <span style={mono(10, "#f3b14a", 700, "0.4px")} className="uppercase">L{l.level}</span>
                <span style={mono(11, "var(--text, #f0f2f8)", 600)}>{l.role}</span>
                <span style={mono(10, "var(--dimmer, #9198b0)")}>after {l.delay_minutes}m</span>
                <span style={mono(9, "var(--dimmer, #9198b0)")}>via {(l.notify_channels || []).join(", ")}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
